/**
 * Editor visual do banner split: produto, nome, preço, CTA e blocos arrastáveis.
 * Preview em escala WYSIWYG (palco ≈ dimensões reais do hero).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Columns2,
  Columns3,
  Columns4,
  Copy,
  ClipboardPaste,
  CopyPlus,
  Loader2,
  Monitor,
  MoreHorizontal,
  Plus,
  RectangleVertical,
  Smartphone,
  Trash2,
  Type,
  Upload,
} from 'lucide-react'
import { adminService } from '../../../services/api'
import { productService } from '../../../services/erpApi'
import ERPProductSearch from '../../../components/ERP/ProductSearch/ERPProductSearch'
import FontFamilyPicker from './FontFamilyPicker'
import { loadGoogleFont } from './loadGoogleFont'
import {
  DEFAULT_BG_FRAME,
  DEFAULT_SPLIT_STYLE,
  MAX_BG_PANELS,
  MAX_TEXT_BLOCKS,
  MIN_BG_PANELS,
  SPLIT_PREVIEW_STAGE,
  bgFrameCss,
  createBgPanel,
  createTextBlock,
  mobilePreviewStageHeight,
  normalizeBgPanels,
  normalizeSplitStyle,
  newTextBlockId,
  panelLooksLikeVideo,
  panelsToLegacyUrls,
  resolvePanelUrl,
  splitFontCss,
} from './splitBannerDefaults'
import {
  applyClipboard,
  captureElement,
  captureStyle,
  getSplitClipboard,
  setSplitClipboard,
  subscribeSplitClipboard,
} from './splitBannerClipboard'
import './SplitBannerComposer.css'

function formatPrice(value) {
  if (value == null || value === '') return null
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value) || 0)
}

function stylesEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b)
}

function parseNumInput(raw) {
  const n = Number(String(raw ?? '').replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

function NumField({ label, value, min, max, step = 0.05, unit, disabled, onChange }) {
  return (
    <label className="split-composer-field split-composer-field--num">
      <span className="split-composer-field-label">{label}</span>
      <div className="split-composer-num">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={Number.isFinite(Number(value)) ? Number(value) : ''}
          disabled={disabled}
          onChange={(e) => {
            const n = parseNumInput(e.target.value)
            if (n == null) return
            onChange(n)
          }}
        />
        {unit ? <span className="split-composer-num-unit">{unit}</span> : null}
      </div>
    </label>
  )
}

function ColorField({ label, value, disabled, onChange }) {
  return (
    <label className="split-composer-field split-composer-field--color">
      <span className="split-composer-field-label">{label}</span>
      <input
        type="color"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  )
}

export default function SplitBannerComposer({
  banner,
  canEdit = true,
  lockedMode = null,
  uploading = false,
  uploadTargetField = null,
  onChange,
  onUpload,
  registerFlush,
}) {
  const [previewMode, setPreviewMode] = useState(
    lockedMode === 'mobile' ? 'mobile' : 'desktop'
  )
  const [gallery, setGallery] = useState([])
  const [galleryLoading, setGalleryLoading] = useState(false)
  const [draftStyle, setDraftStyle] = useState(() =>
    normalizeSplitStyle(banner?.split_style)
  )
  const [selectedBlockId, setSelectedBlockId] = useState(null)
  const [nameSelected, setNameSelected] = useState(false)
  const [productSelected, setProductSelected] = useState(false)
  const [priceSelected, setPriceSelected] = useState(false)
  const [ctaSelected, setCtaSelected] = useState(false)
  const [bgSelected, setBgSelected] = useState(null) // panel index | null
  const [menuOpen, setMenuOpen] = useState(false)
  const [editorSection, setEditorSection] = useState(null) // name|price|cta|texts|null
  const [clipboardTick, setClipboardTick] = useState(0)
  const [previewScale, setPreviewScale] = useState(1)

  const wrapRef = useRef(null)
  const canvasRef = useRef(null)
  const dragTargetRef = useRef(null) // 'product' | 'meta' | 'price' | 'cta' | `block:${id}`
  const transformRef = useRef(null) // text block scale/width/rotate session
  const draggingRef = useRef(false)
  const draftRef = useRef(draftStyle)
  const previewModeRef = useRef(previewMode)

  draftRef.current = draftStyle
  previewModeRef.current = previewMode

  useEffect(() => subscribeSplitClipboard(() => setClipboardTick((n) => n + 1)), [])

  useEffect(() => {
    if (lockedMode === 'desktop' || lockedMode === 'mobile') {
      setPreviewMode(lockedMode)
    }
  }, [lockedMode])

  useEffect(() => {
    if (!menuOpen) return undefined
    const onDoc = (e) => {
      if (e.target?.closest?.('.split-composer-float')) return
      setMenuOpen(false)
    }
    document.addEventListener('pointerdown', onDoc)
    return () => document.removeEventListener('pointerdown', onDoc)
  }, [menuOpen])

  const clearSelection = useCallback(() => {
    setProductSelected(false)
    setNameSelected(false)
    setPriceSelected(false)
    setCtaSelected(false)
    setSelectedBlockId(null)
    setBgSelected(null)
    setMenuOpen(false)
  }, [])

  const selectTarget = useCallback((target) => {
    setMenuOpen(false)
    setBgSelected(null)
    if (target === 'product') {
      setProductSelected(true)
      setNameSelected(false)
      setPriceSelected(false)
      setCtaSelected(false)
      setSelectedBlockId(null)
    } else if (target === 'meta') {
      setNameSelected(true)
      setProductSelected(false)
      setPriceSelected(false)
      setCtaSelected(false)
      setSelectedBlockId(null)
    } else if (target === 'price') {
      setPriceSelected(true)
      setProductSelected(false)
      setNameSelected(false)
      setCtaSelected(false)
      setSelectedBlockId(null)
    } else if (target === 'cta') {
      setCtaSelected(true)
      setProductSelected(false)
      setNameSelected(false)
      setPriceSelected(false)
      setSelectedBlockId(null)
    } else if (String(target).startsWith('block:')) {
      setSelectedBlockId(String(target).slice(6))
      setProductSelected(false)
      setNameSelected(false)
      setPriceSelected(false)
      setCtaSelected(false)
    } else {
      clearSelection()
    }
  }, [clearSelection])

  const stage =
    previewMode === 'desktop'
      ? SPLIT_PREVIEW_STAGE.desktop
      : {
          width: SPLIT_PREVIEW_STAGE.mobile.width,
          height: mobilePreviewStageHeight(draftStyle.viewport_height_mobile),
        }

  useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const update = () => {
      const pad = 24
      const avail = Math.max(120, el.clientWidth - pad)
      setPreviewScale(Math.min(1, avail / stage.width))
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [stage.width])

  // Sincroniza do banner salvo quando não está arrastando
  useEffect(() => {
    if (draggingRef.current) return
    const count = normalizeBgPanels(banner?.bg_panels, banner || {}).length
    const next = normalizeSplitStyle(banner?.split_style, count)
    setDraftStyle((prev) => (stylesEqual(prev, next) ? prev : next))
  }, [banner?.split_style, banner?.bg_panels, banner?.id, banner])

  const commitStyle = useCallback(
    (nextStyle) => {
      const count = normalizeBgPanels(banner?.bg_panels, banner || {}).length
      const normalized = normalizeSplitStyle(nextStyle, count)
      setDraftStyle(normalized)
      draftRef.current = normalized
      if (!canEdit || typeof onChange !== 'function') return normalized
      onChange((current) => ({
        ...current,
        split_style: normalized,
      }))
      return normalized
    },
    [canEdit, onChange, banner]
  )

  const patchBanner = useCallback(
    (partialOrFn) => {
      if (!canEdit || typeof onChange !== 'function') return
      onChange(partialOrFn)
    },
    [canEdit, onChange]
  )

  // Nome/preço reais quando o banner só tem product_id (após reload)
  useEffect(() => {
    let cancelled = false
    const productId = banner?.product_id
    if (!productId || banner.product_name?.trim()) return undefined

    ;(async () => {
      try {
        const res = await productService.getById(productId)
        const product = res?.data || res
        if (cancelled || !product) return
        patchBanner((current) => {
          if (current.product_id !== productId) return current
          if (current.product_name?.trim()) return current
          return {
            ...current,
            product_name: product.name || '',
            product_price:
              current.product_price ?? product.price ?? product.sale_price ?? null,
            product_image_url:
              current.product_image_url || product.image_url || '',
          }
        })
      } catch {
        /* ignore */
      }
    })()

    return () => {
      cancelled = true
    }
  }, [banner?.product_id, banner?.product_name, patchBanner])

  const style = draftStyle
  const placement = previewMode === 'desktop' ? style.desktop : style.mobile
  const bgMode = previewMode === 'desktop' ? 'desktop' : 'mobile'
  const panels = normalizeBgPanels(banner?.bg_panels, banner || {})
  const bgFrames = Array.isArray(style.bg?.[bgMode])
    ? style.bg[bgMode]
    : panels.map(() => ({ ...DEFAULT_BG_FRAME }))
  const filledCount = panels.filter((p) =>
    Boolean(resolvePanelUrl(p, previewMode))
  ).length
  const singleBg = filledCount === 1

  const patchPanels = (nextPanels) => {
    const list = normalizeBgPanels(nextPanels, {})
    const nextStyle = normalizeSplitStyle(draftRef.current, list.length)
    draftRef.current = nextStyle
    setDraftStyle(nextStyle)
    patchBanner({
      bg_panels: list,
      ...panelsToLegacyUrls(list),
      split_style: nextStyle,
    })
  }

  const setPanelCount = (count) => {
    if (!canEdit) return
    const n = Math.min(MAX_BG_PANELS, Math.max(MIN_BG_PANELS, count))
    setBgSelected(null)

    if (previewMode === 'mobile') {
      // Não apaga painéis do desktop: só oculta/exibe no mobile
      let next = [...panels]
      while (next.length < n) next.push(createBgPanel())
      next = next.map((p, i) => {
        if (i < n) {
          // Slot visível no mobile: tira __none__ para herdar desktop
          return p.image_url_mobile === '__none__'
            ? { ...p, image_url_mobile: null }
            : p
        }
        // Slot oculto só no mobile
        return { ...p, image_url_mobile: '__none__' }
      })
      patchPanels(next)
      return
    }

    // Desktop: define a quantidade real de painéis
    let next = [...panels]
    while (next.length < n) next.push(createBgPanel())
    next = next.slice(0, n).map((p) => ({
      ...p,
      // Limpa ocultações mobile órfãs ao reduzir/expandir desktop
      image_url_mobile:
        p.image_url_mobile === '__none__' ? null : p.image_url_mobile,
    }))
    patchPanels(next)
  }

  const activePanelCount =
    previewMode === 'mobile'
      ? panels.filter((p) => p.image_url_mobile !== '__none__').length
      : panels.length

  const visiblePanelEntries = panels
    .map((panel, index) => ({ panel, index }))
    .filter(({ panel }) => {
      if (previewMode === 'mobile' && panel.image_url_mobile === '__none__') {
        return false
      }
      return true
    })

  const updateBgFrame = (index, patch) => {
    const mode = previewModeRef.current === 'desktop' ? 'desktop' : 'mobile'
    const prev = draftRef.current
    const frames = [...(prev.bg?.[mode] || [])]
    while (frames.length <= index) frames.push({ ...DEFAULT_BG_FRAME })
    frames[index] = { ...frames[index], ...patch }
    const next = normalizeSplitStyle(
      {
        ...prev,
        bg: {
          ...prev.bg,
          [mode]: frames,
        },
      },
      panels.length
    )
    draftRef.current = next
    setDraftStyle(next)
  }

  const commitBgFrame = () => commitStyle(draftRef.current)

  const clearBgPanel = (index) => {
    const next = panels.map((p, i) => {
      if (i !== index) return p
      if (previewMode === 'mobile') {
        return { ...p, image_url_mobile: '__none__' }
      }
      return { ...p, image_url: '' }
    })
    patchPanels(next)
  }

  const makeBgPanHandlers = (index) => ({
    onPointerDown: (e) => {
      if (!canEdit) return
      if (e.target.closest('.split-composer-half-btn')) return
      e.preventDefault()
      e.stopPropagation()
      clearSelection()
      setBgSelected(index)
      const mode = previewModeRef.current === 'desktop' ? 'desktop' : 'mobile'
      const frame =
        draftRef.current.bg?.[mode]?.[index] || { ...DEFAULT_BG_FRAME }
      const startX = e.clientX
      const startY = e.clientY
      const origin = { x: frame.x, y: frame.y }
      const el = e.currentTarget
      const rect = el.getBoundingClientRect()
      draggingRef.current = true
      dragTargetRef.current = `bg:${index}`
      el.setPointerCapture?.(e.pointerId)

      const onMove = (ev) => {
        if (!draggingRef.current) return
        const dx = ((ev.clientX - startX) / Math.max(1, rect.width)) * 100
        const dy = ((ev.clientY - startY) / Math.max(1, rect.height)) * 100
        updateBgFrame(index, {
          x: Math.min(100, Math.max(0, origin.x - dx)),
          y: Math.min(100, Math.max(0, origin.y - dy)),
        })
      }
      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        window.removeEventListener('pointercancel', onUp)
        draggingRef.current = false
        dragTargetRef.current = null
        commitBgFrame()
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
    },
    onWheel: (e) => {
      if (!canEdit) return
      // Zoom só com Ctrl + scroll (evita zoom acidental)
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      e.stopPropagation()
      setBgSelected(index)
      const mode = previewModeRef.current === 'desktop' ? 'desktop' : 'mobile'
      const frame =
        draftRef.current.bg?.[mode]?.[index] || { ...DEFAULT_BG_FRAME }
      const delta = e.deltaY > 0 ? -8 : 8
      const nextScale = Math.min(
        280,
        Math.max(100, (frame.scale || 100) + delta)
      )
      updateBgFrame(index, { scale: nextScale })
      commitBgFrame()
    },
  })

  const fontSize =
    previewMode === 'desktop' ? style.font_size_desktop : style.font_size_mobile
  const priceFontSize =
    previewMode === 'desktop'
      ? style.price_font_size_desktop
      : style.price_font_size_mobile
  const ctaFontSize =
    previewMode === 'desktop'
      ? style.cta_font_size_desktop
      : style.cta_font_size_mobile

  useEffect(() => {
    loadGoogleFont(style.font_family)
    loadGoogleFont(style.price_font_family)
    loadGoogleFont(style.cta_font_family)
    ;(style.text_blocks || []).forEach((b) => loadGoogleFont(b.font_family))
  }, [
    style.font_family,
    style.price_font_family,
    style.cta_font_family,
    style.text_blocks,
  ])

  useEffect(() => {
    if (typeof registerFlush !== 'function') return undefined
    registerFlush(() => commitStyle(draftRef.current))
    return () => {
      if (typeof registerFlush === 'function') registerFlush(null)
    }
  }, [registerFlush, commitStyle])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!banner?.product_id) {
        setGallery([])
        return
      }
      setGalleryLoading(true)
      try {
        const res = await adminService.getProductImages(banner.product_id)
        if (cancelled) return
        const list = Array.isArray(res?.data) ? res.data : []
        setGallery(list)
        const showProductImage =
          normalizeSplitStyle(banner.split_style).show_product_image !== false
        if (
          showProductImage &&
          !banner.product_image_url &&
          list[0]?.image_url
        ) {
          const match = banner.product_image_id
            ? list.find((img) => Number(img.id) === Number(banner.product_image_id))
            : list[0]
          if (match?.image_url) {
            patchBanner({
              product_image_url: match.image_url,
              product_image_id: match.id ?? banner.product_image_id ?? null,
            })
          }
        }
      } catch {
        if (!cancelled) setGallery([])
      } finally {
        if (!cancelled) setGalleryLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banner?.product_id])

  const handleSelectProduct = async (product) => {
    let galleryUrl = product.image_url || ''
    let imageId = null
    try {
      const res = await adminService.getProductImages(product.id)
      const list = Array.isArray(res?.data) ? res.data : []
      setGallery(list)
      if (list[0]) {
        galleryUrl = list[0].image_url || galleryUrl
        imageId = list[0].id ?? null
      }
    } catch {
      setGallery([])
    }
    patchBanner({
      product_id: product.id,
      product_name: product.name || product.product_name || '',
      product_price: product.price ?? product.sale_price ?? null,
      product_image_url: galleryUrl || '',
      product_image_id: imageId,
      link_url: `/products/${product.id}`,
      alt: product.name || product.product_name || '',
      split_style: normalizeSplitStyle({
        ...draftRef.current,
        show_product_image: Boolean(galleryUrl),
      }),
    })
  }

  const selectGalleryImage = (img) => {
    patchBanner({
      product_image_id: img.id,
      product_image_url: img.image_url || '',
      split_style: normalizeSplitStyle({
        ...draftRef.current,
        show_product_image: true,
      }),
    })
  }

  const clearProductImage = () => {
    patchBanner({
      product_image_id: null,
      product_image_url: '',
      split_style: normalizeSplitStyle({
        ...draftRef.current,
        show_product_image: false,
      }),
    })
  }

  const applyPointerToDraft = (clientX, clientY, target) => {
    const el = canvasRef.current
    if (!el || !target) return
    const rect = el.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return
    const x = Math.min(95, Math.max(5, ((clientX - rect.left) / rect.width) * 100))
    const y = Math.min(95, Math.max(5, ((clientY - rect.top) / rect.height) * 100))
    const mode = previewModeRef.current === 'desktop' ? 'desktop' : 'mobile'
    const prev = draftRef.current

    let next
    if (target === 'product') {
      next = {
        ...prev,
        [mode]: { ...prev[mode], x, y },
      }
    } else if (target === 'meta') {
      next = {
        ...prev,
        [mode]: { ...prev[mode], meta_x: x, meta_y: y },
      }
    } else if (target === 'price') {
      next = {
        ...prev,
        [mode]: { ...prev[mode], price_x: x, price_y: y },
      }
    } else if (target === 'cta') {
      next = {
        ...prev,
        [mode]: { ...prev[mode], cta_x: x, cta_y: y },
      }
    } else if (String(target).startsWith('block:')) {
      const blockId = String(target).slice(6)
      next = {
        ...prev,
        text_blocks: (prev.text_blocks || []).map((b) =>
          b.id === blockId
            ? { ...b, [mode]: { ...b[mode], x, y } }
            : b
        ),
      }
    } else {
      return
    }

    const normalized = normalizeSplitStyle(next)
    draftRef.current = normalized
    setDraftStyle(normalized)
  }

  const applyOverlayTransform = (clientX, clientY) => {
    const session = transformRef.current
    const el = canvasRef.current
    if (!session || !el) return
    const rect = el.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return

    const mode = previewModeRef.current === 'desktop' ? 'desktop' : 'mobile'
    const prev = draftRef.current
    const isMeta = session.target === 'meta'

    if (session.kind === 'scale') {
      const cx = rect.left + (session.centerX / 100) * rect.width
      const cy = rect.top + (session.centerY / 100) * rect.height
      const dist = Math.max(8, Math.hypot(clientX - cx, clientY - cy))
      const factor = dist / session.startDist

      if (session.target === 'product') {
        const nextScale = Math.min(
          180,
          Math.max(40, Math.round(session.startScale * factor))
        )
        const next = {
          ...prev,
          [mode]: { ...prev[mode], scale: nextScale },
        }
        const normalized = normalizeSplitStyle(next)
        draftRef.current = normalized
        setDraftStyle(normalized)
        return
      }

      const fontKey =
        mode === 'desktop' ? 'font_size_desktop' : 'font_size_mobile'
      const max = mode === 'desktop' ? (isMeta ? 3.5 : 4) : isMeta ? 2.5 : 3
      const min = isMeta ? 0.75 : 0.6
      const nextFont = Math.min(
        max,
        Math.max(min, Number((session.startFont * factor).toFixed(2)))
      )
      const next = isMeta
        ? { ...prev, [fontKey]: nextFont }
        : {
            ...prev,
            text_blocks: (prev.text_blocks || []).map((b) =>
              b.id === session.target ? { ...b, [fontKey]: nextFont } : b
            ),
          }
      const normalized = normalizeSplitStyle(next)
      draftRef.current = normalized
      setDraftStyle(normalized)
      return
    }

    if (session.kind === 'width') {
      const dxPct = ((clientX - session.startClientX) / rect.width) * 100
      const delta = session.handle === 'e' ? dxPct : -dxPct
      const nextWidth = Math.min(
        session.target === 'cta' ? 90 : 92,
        Math.max(
          session.target === 'cta' ? 8 : 12,
          Number((session.startWidth + delta).toFixed(1))
        )
      )
      let next
      if (session.target === 'cta') {
        next = {
          ...prev,
          [mode]: { ...prev[mode], cta_w: nextWidth },
        }
      } else if (isMeta) {
        next = {
          ...prev,
          [mode]: { ...prev[mode], meta_width: nextWidth },
        }
      } else {
        next = {
          ...prev,
          text_blocks: (prev.text_blocks || []).map((b) =>
            b.id === session.target
              ? { ...b, [mode]: { ...b[mode], width: nextWidth } }
              : b
          ),
        }
      }
      const normalized = normalizeSplitStyle(next)
      draftRef.current = normalized
      setDraftStyle(normalized)
      return
    }

    if (session.kind === 'height') {
      const dyPct = ((clientY - session.startClientY) / rect.height) * 100
      const delta = session.handle === 's' ? dyPct : -dyPct
      const nextHeight = Math.min(
        60,
        Math.max(4, Number((session.startHeight + delta).toFixed(1)))
      )
      if (session.target !== 'cta') return
      const next = {
        ...prev,
        [mode]: { ...prev[mode], cta_h: nextHeight },
      }
      const normalized = normalizeSplitStyle(next)
      draftRef.current = normalized
      setDraftStyle(normalized)
      return
    }

    if (session.kind === 'rotate') {
      const cx = rect.left + (session.centerX / 100) * rect.width
      const cy = rect.top + (session.centerY / 100) * rect.height
      const angle =
        (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI
      let nextRotate = session.startRotate + (angle - session.startAngle)
      while (nextRotate > 180) nextRotate -= 360
      while (nextRotate < -180) nextRotate += 360
      nextRotate = Number(nextRotate.toFixed(1))
      let next
      if (session.target === 'product') {
        next = {
          ...prev,
          [mode]: { ...prev[mode], rotate: nextRotate },
        }
      } else if (isMeta) {
        next = {
          ...prev,
          [mode]: { ...prev[mode], meta_rotate: nextRotate },
        }
      } else {
        next = {
          ...prev,
          text_blocks: (prev.text_blocks || []).map((b) =>
            b.id === session.target
              ? { ...b, [mode]: { ...b[mode], rotate: nextRotate } }
              : b
          ),
        }
      }
      const normalized = normalizeSplitStyle(next)
      draftRef.current = normalized
      setDraftStyle(normalized)
    }
  }

  const endInteraction = (e) => {
    draggingRef.current = false
    dragTargetRef.current = null
    transformRef.current = null
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId)
    } catch {
      /* ignore */
    }
    commitStyle(draftRef.current)
  }

  const makeDragHandlers = (target) => ({
    onPointerDown: (e) => {
      if (!canEdit) return
      e.preventDefault()
      e.stopPropagation()
      draggingRef.current = true
      transformRef.current = null
      dragTargetRef.current = target
      selectTarget(target)
      e.currentTarget.setPointerCapture?.(e.pointerId)
      applyPointerToDraft(e.clientX, e.clientY, target)
    },
    onPointerMove: (e) => {
      if (!draggingRef.current || !canEdit) return
      if (transformRef.current) {
        applyOverlayTransform(e.clientX, e.clientY)
        return
      }
      applyPointerToDraft(e.clientX, e.clientY, dragTargetRef.current)
    },
    onPointerUp: endInteraction,
    onPointerCancel: endInteraction,
  })

  const makeTransformHandleHandlers = (target, kind, handle) => ({
    onPointerDown: (e) => {
      if (!canEdit) return
      e.preventDefault()
      e.stopPropagation()
      const mode = previewModeRef.current === 'desktop' ? 'desktop' : 'mobile'
      const draft = draftRef.current
      const canvas = canvasRef.current?.getBoundingClientRect()
      if (!canvas) return

      let centerX = 50
      let centerY = 50
      let startFont = 1
      let startWidth = 40
      let startHeight = 9
      let startRotate = 0
      let startScale = 100

      if (target === 'product') {
        const place = draft[mode] || {}
        centerX = Number(place.x) || 50
        centerY = Number(place.y) || 50
        startScale = Number(place.scale) || 100
        startRotate = Number(place.rotate) || 0
        selectTarget('product')
      } else if (target === 'meta') {
        const place = draft[mode] || {}
        centerX = Number(place.meta_x) || 50
        centerY = Number(place.meta_y) || 50
        startFont =
          Number(
            mode === 'desktop' ? draft.font_size_desktop : draft.font_size_mobile
          ) || 1.25
        startWidth = Number(place.meta_width) || 40
        startRotate = Number(place.meta_rotate) || 0
        selectTarget('meta')
      } else if (target === 'cta') {
        const place = draft[mode] || {}
        centerX = Number(place.cta_x) || 50
        centerY = Number(place.cta_y) || 50
        startWidth = Number(place.cta_w) || 22
        startHeight = Number(place.cta_h) || 9
        selectTarget('cta')
      } else {
        const block = (draft.text_blocks || []).find((b) => b.id === target)
        if (!block) return
        const place = block[mode] || {}
        centerX = Number(place.x) || 50
        centerY = Number(place.y) || 50
        startFont =
          Number(
            mode === 'desktop' ? block.font_size_desktop : block.font_size_mobile
          ) || 1
        startWidth = Number(place.width) || 40
        startRotate = Number(place.rotate) || 0
        selectTarget(`block:${target}`)
      }

      const cx = canvas.left + (centerX / 100) * canvas.width
      const cy = canvas.top + (centerY / 100) * canvas.height
      const startDist = Math.max(8, Math.hypot(e.clientX - cx, e.clientY - cy))
      const startAngle =
        (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI

      draggingRef.current = true
      dragTargetRef.current = null
      transformRef.current = {
        kind,
        handle,
        target,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startFont,
        startWidth,
        startHeight,
        startRotate,
        startScale,
        centerX,
        centerY,
        startDist,
        startAngle,
      }

      // Listeners no window — o palco usa transform:scale e o capture no handle falhava
      const onMove = (ev) => {
        if (!transformRef.current) return
        applyOverlayTransform(ev.clientX, ev.clientY)
      }
      const onUp = (ev) => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        window.removeEventListener('pointercancel', onUp)
        endInteraction(ev)
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
    },
  })

  const renderCtaHandles = () => (
    <>
      <span
        className="split-composer-text-handle is-e"
        {...makeTransformHandleHandlers('cta', 'width', 'e')}
      />
      <span
        className="split-composer-text-handle is-w"
        {...makeTransformHandleHandlers('cta', 'width', 'w')}
      />
      <span
        className="split-composer-text-handle is-n"
        {...makeTransformHandleHandlers('cta', 'height', 'n')}
      />
      <span
        className="split-composer-text-handle is-s"
        {...makeTransformHandleHandlers('cta', 'height', 's')}
      />
    </>
  )

  const renderTransformHandles = (target) => (
    <>
      <span
        className="split-composer-text-handle is-nw"
        {...makeTransformHandleHandlers(target, 'scale', 'nw')}
      />
      <span
        className="split-composer-text-handle is-ne"
        {...makeTransformHandleHandlers(target, 'scale', 'ne')}
      />
      <span
        className="split-composer-text-handle is-se"
        {...makeTransformHandleHandlers(target, 'scale', 'se')}
      />
      <span
        className="split-composer-text-handle is-sw"
        {...makeTransformHandleHandlers(target, 'scale', 'sw')}
      />
      {target !== 'product' ? (
        <>
          <span
            className="split-composer-text-handle is-e"
            {...makeTransformHandleHandlers(target, 'width', 'e')}
          />
          <span
            className="split-composer-text-handle is-w"
            {...makeTransformHandleHandlers(target, 'width', 'w')}
          />
        </>
      ) : null}
      <span
        className="split-composer-text-handle is-rotate"
        title="Girar"
        {...makeTransformHandleHandlers(target, 'rotate', 'rotate')}
      />
    </>
  )

  const productImg =
    style.show_product_image !== false
      ? banner.product_image_url || ''
      : ''
  const scaleFactor = (placement.scale || 100) / 100
  // Largura base ≈ loja; escala do compositor mexe nessa % (sempre muda o box)
  const productWidthPct = Number(
    (
      (previewMode === 'desktop' ? 28 : 42) * scaleFactor
    ).toFixed(1)
  )
  const productDrag = makeDragHandlers('product')
  const metaDrag = makeDragHandlers('meta')
  const priceDrag = makeDragHandlers('price')
  const ctaDrag = makeDragHandlers('cta')
  const selectedBlock =
    (style.text_blocks || []).find((b) => b.id === selectedBlockId) || null

  const activeSelection = productSelected
    ? { kind: 'product' }
    : nameSelected
      ? { kind: 'meta' }
      : priceSelected
        ? { kind: 'price' }
        : ctaSelected
          ? { kind: 'cta' }
          : selectedBlockId
            ? { kind: 'block', blockId: selectedBlockId }
            : null

  const selectionLabel = !activeSelection
    ? ''
    : activeSelection.kind === 'product'
      ? 'Produto'
      : activeSelection.kind === 'meta'
        ? 'Nome'
        : activeSelection.kind === 'price'
          ? 'Preço'
          : activeSelection.kind === 'cta'
            ? 'Botão'
            : 'Texto'

  const clipboardPayload = getSplitClipboard()
  // clipboardTick força re-render quando o clipboard muda
  void clipboardTick

  const canPaste = Boolean(clipboardPayload)

  const handleCopy = (mode) => {
    if (!activeSelection || !canEdit) return
    const block =
      activeSelection.kind === 'block' ? selectedBlock : null
    const payload =
      mode === 'style'
        ? captureStyle(activeSelection.kind, style, block)
        : captureElement(activeSelection.kind, style, block)
    if (payload) setSplitClipboard(payload)
    setMenuOpen(false)
  }

  const handlePaste = () => {
    if (!canEdit || !clipboardPayload) return

    // Texto completo sem seleção compatível → insere novo bloco
    if (
      clipboardPayload.kind === 'text_block' &&
      clipboardPayload.mode === 'full' &&
      activeSelection?.kind !== 'block'
    ) {
      if ((style.text_blocks || []).length >= MAX_TEXT_BLOCKS) return
      const snap = clipboardPayload.snapshot
      const block = createTextBlock({
        ...snap,
        id: newTextBlockId(),
        desktop: {
          ...snap.desktop,
          x: Math.min(95, (snap.desktop?.x || 50) + 4),
          y: Math.min(95, (snap.desktop?.y || 22) + 4),
        },
        mobile: {
          ...snap.mobile,
          x: Math.min(95, (snap.mobile?.x || 50) + 4),
          y: Math.min(95, (snap.mobile?.y || 18) + 4),
        },
      })
      commitStyle({
        ...style,
        text_blocks: [...(style.text_blocks || []), block],
      })
      selectTarget(`block:${block.id}`)
      setMenuOpen(false)
      return
    }

    if (!activeSelection) {
      setMenuOpen(false)
      return
    }

    const result = applyClipboard(clipboardPayload, style, activeSelection)
    if (!result?.style) {
      setMenuOpen(false)
      return
    }
    commitStyle(result.style)
    setMenuOpen(false)
  }

  const handleDuplicate = () => {
    if (!canEdit || activeSelection?.kind !== 'block' || !selectedBlock) return
    if ((style.text_blocks || []).length >= MAX_TEXT_BLOCKS) return
    const block = createTextBlock({
      ...selectedBlock,
      id: newTextBlockId(),
      desktop: {
        ...selectedBlock.desktop,
        x: Math.min(95, (selectedBlock.desktop?.x || 50) + 4),
        y: Math.min(95, (selectedBlock.desktop?.y || 22) + 4),
      },
      mobile: {
        ...selectedBlock.mobile,
        x: Math.min(95, (selectedBlock.mobile?.x || 50) + 4),
        y: Math.min(95, (selectedBlock.mobile?.y || 18) + 4),
      },
    })
    commitStyle({
      ...style,
      text_blocks: [...(style.text_blocks || []), block],
    })
    selectTarget(`block:${block.id}`)
    setMenuOpen(false)
  }

  const handleDelete = () => {
    if (!canEdit || !activeSelection) return
    if (activeSelection.kind === 'block') {
      commitStyle({
        ...style,
        text_blocks: (style.text_blocks || []).filter(
          (b) => b.id !== activeSelection.blockId
        ),
      })
      clearSelection()
      return
    }
    if (activeSelection.kind === 'product') {
      commitStyle({ ...style, show_product_image: false })
      patchBanner({
        product_image_id: null,
        product_image_url: '',
      })
      clearSelection()
      return
    }
    if (activeSelection.kind === 'meta') {
      commitStyle({ ...style, show_product_name: false })
      clearSelection()
      return
    }
    if (activeSelection.kind === 'price') {
      commitStyle({ ...style, show_product_price: false })
      clearSelection()
      return
    }
    if (activeSelection.kind === 'cta') {
      commitStyle({ ...style, show_cta: false })
      clearSelection()
    }
  }

  const renderElementFloat = (counterRotate = 0) => {
    if (!canEdit || !activeSelection) return null
    return (
      <div
        className="split-composer-float"
        role="toolbar"
        aria-label="Ações do elemento"
        style={{
          transform: `translateX(-50%) rotate(${-Number(counterRotate) || 0}deg)`,
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <span className="split-composer-float-label">{selectionLabel}</span>
        <div className="split-composer-float-actions">
          <div className="split-composer-float-more">
            <button
              type="button"
              className="split-composer-float-btn"
              aria-label="Mais opções"
              aria-expanded={menuOpen}
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpen((o) => !o)
              }}
            >
              <MoreHorizontal size={16} />
            </button>
            {menuOpen ? (
              <div className="split-composer-float-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleCopy('full')}
                >
                  <Copy size={14} />
                  Copiar
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleCopy('style')}
                >
                  <Copy size={14} />
                  Copiar estilo
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={!canPaste}
                  onClick={handlePaste}
                >
                  <ClipboardPaste size={14} />
                  Colar
                  {clipboardPayload ? (
                    <span className="split-composer-float-hint">
                      {clipboardPayload.mode === 'style' ? 'estilo' : 'elemento'}
                    </span>
                  ) : null}
                </button>
                {activeSelection.kind === 'block' ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleDuplicate}
                  >
                    <CopyPlus size={14} />
                    Duplicar
                  </button>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  className="is-danger"
                  onClick={handleDelete}
                >
                  <Trash2 size={14} />
                  Excluir
                </button>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="split-composer-float-btn is-danger"
            aria-label="Excluir elemento"
            onClick={(e) => {
              e.stopPropagation()
              handleDelete()
            }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    )
  }

  const addTextBlock = () => {
    if ((style.text_blocks || []).length >= MAX_TEXT_BLOCKS) return
    const block = createTextBlock()
    const next = normalizeSplitStyle({
      ...style,
      text_blocks: [...(style.text_blocks || []), block],
    })
    commitStyle(next)
    selectTarget(`block:${block.id}`)
  }

  const updateTextBlock = (blockId, partial) => {
    const next = normalizeSplitStyle({
      ...style,
      text_blocks: (style.text_blocks || []).map((b) =>
        b.id === blockId ? { ...b, ...partial } : b
      ),
    })
    commitStyle(next)
  }

  const removeTextBlock = (blockId) => {
    commitStyle({
      ...style,
      text_blocks: (style.text_blocks || []).filter((b) => b.id !== blockId),
    })
    if (selectedBlockId === blockId) setSelectedBlockId(null)
  }

  return (
    <div className="split-composer">
      <div className="split-composer-toolbar">
        {lockedMode ? null : (
        <div className="split-composer-mode" role="group" aria-label="Breakpoint">
          <button
            type="button"
            className={previewMode === 'desktop' ? 'is-active' : ''}
            disabled={!canEdit}
            onClick={() => setPreviewMode('desktop')}
          >
            <Monitor size={14} />
            Desktop
          </button>
          <button
            type="button"
            className={previewMode === 'mobile' ? 'is-active' : ''}
            disabled={!canEdit}
            onClick={() => setPreviewMode('mobile')}
          >
            <Smartphone size={14} />
            Mobile
          </button>
        </div>
        )}

        <div className="split-composer-mode" role="group" aria-label="Divisão de fotos">
          <button
            type="button"
            className={activePanelCount === 1 ? 'is-active' : ''}
            disabled={!canEdit}
            title={
              previewMode === 'mobile'
                ? '1 foto no mobile (mantém desktop)'
                : '1 foto'
            }
            onClick={() => setPanelCount(1)}
          >
            <RectangleVertical size={14} />
            1
          </button>
          <button
            type="button"
            className={activePanelCount === 2 ? 'is-active' : ''}
            disabled={!canEdit}
            title={
              previewMode === 'mobile'
                ? '2 fotos no mobile'
                : '2 fotos'
            }
            onClick={() => setPanelCount(2)}
          >
            <Columns2 size={14} />
            2
          </button>
          <button
            type="button"
            className={activePanelCount === 3 ? 'is-active' : ''}
            disabled={!canEdit}
            title={
              previewMode === 'mobile'
                ? '3 fotos no mobile'
                : '3 fotos'
            }
            onClick={() => setPanelCount(3)}
          >
            <Columns3 size={14} />
            3
          </button>
          <button
            type="button"
            className={activePanelCount === 4 ? 'is-active' : ''}
            disabled={!canEdit}
            title={
              previewMode === 'mobile'
                ? '4 fotos no mobile'
                : '4 fotos'
            }
            onClick={() => setPanelCount(4)}
          >
            <Columns4 size={14} />
            4
          </button>
        </div>

        <div
          className="split-composer-mode split-composer-elements-tabs"
          role="group"
          aria-label="Elementos do banner"
        >
          {[
            { id: 'name', label: 'Nome' },
            { id: 'price', label: 'Preço' },
            { id: 'cta', label: 'Botão' },
            { id: 'texts', label: 'Textos' },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              className={editorSection === item.id ? 'is-active' : ''}
              disabled={!canEdit}
              onClick={() =>
                setEditorSection((cur) => (cur === item.id ? null : item.id))
              }
            >
              {item.id === 'texts' ? <Type size={14} /> : null}
              {item.label}
            </button>
          ))}
        </div>

        <p className="split-composer-hint">
          Arraste para enquadrar. Zoom: Ctrl + scroll. Aceita foto ou vídeo
          (mp4).
          {lockedMode
            ? ' Este slide vale só para o dispositivo escolhido.'
            : ' No mobile, 1–4 oculta painéis sem apagar o desktop.'}
        </p>
      </div>

      <div
        ref={wrapRef}
        className={`split-composer-canvas-wrap ${
          previewMode === 'mobile' ? 'is-mobile' : 'is-desktop'
        }`}
      >
        <div
          className="split-composer-scale-frame"
          style={{
            width: stage.width * previewScale,
            height: stage.height * previewScale,
          }}
          onPointerDown={(e) => {
            if (e.target === e.currentTarget) clearSelection()
          }}
        >
          <div
            ref={canvasRef}
            className={`split-composer-canvas ${
              previewMode === 'mobile' ? 'is-mobile' : 'is-desktop'
            }`}
            style={{
              width: stage.width,
              height: stage.height,
              transform: `scale(${previewScale})`,
            }}
          >
          <div
            className={`split-composer-bg ${
              visiblePanelEntries.length <= 1 ? 'is-single' : ''
            } panels-${visiblePanelEntries.length}`}
            onPointerDown={(e) => {
              if (
                !e.target.closest(
                  '.split-composer-half, .split-composer-product, .split-composer-meta, .split-composer-price, .split-composer-cta, .split-composer-text-block, .split-composer-text-handle, .split-composer-half-btn, .split-composer-float'
                )
              ) {
                clearSelection()
              }
            }}
          >
            {visiblePanelEntries.map(({ panel, index }) => {
              const url = resolvePanelUrl(panel, previewMode)
              const hasImg = Boolean(url)
              const frame = bgFrames[index] || DEFAULT_BG_FRAME
              const uploadField =
                previewMode === 'mobile'
                  ? `panel:${index}:mobile`
                  : `panel:${index}`
              const isOnlyFilled =
                visiblePanelEntries.length === 1 || (singleBg && hasImg)
              return (
                <div
                  key={panel.id || `panel-${index}`}
                  className={`split-composer-half ${
                    !hasImg ? 'is-empty' : ''
                  } ${bgSelected === index ? 'is-bg-selected' : ''} ${
                    isOnlyFilled ? 'is-full' : ''
                  }`}
                  style={{
                    flex: `1 1 ${100 / Math.max(1, visiblePanelEntries.length)}%`,
                  }}
                  {...(hasImg ? makeBgPanHandlers(index) : {})}
                >
                  <div className="split-composer-half-frame">
                    {hasImg ? (
                      panelLooksLikeVideo(url) ? (
                        <video
                          key={`vid-${previewMode}-${panel.id || index}-${url}`}
                          src={url}
                          muted
                          loop
                          autoPlay
                          playsInline
                          draggable={false}
                          style={bgFrameCss(frame)}
                        />
                      ) : (
                        <img
                          key={`img-${previewMode}-${panel.id || index}-${url}`}
                          src={url}
                          alt=""
                          draggable={false}
                          style={bgFrameCss(frame)}
                        />
                      )
                    ) : (
                      <span className="split-composer-half-placeholder">
                        Mídia {index + 1}
                        <small>foto ou vídeo</small>
                      </span>
                    )}
                  </div>
                  {canEdit ? (
                    <button
                      type="button"
                      className="split-composer-half-btn"
                      disabled={uploading}
                      onClick={(e) => {
                        e.stopPropagation()
                        onUpload?.(uploadField)
                      }}
                      title={
                        previewMode === 'mobile'
                          ? `Enviar foto/vídeo ${index + 1} (mobile)`
                          : `Enviar foto/vídeo ${index + 1}`
                      }
                      aria-label={`Enviar mídia ${index + 1}`}
                    >
                      {uploading && uploadTargetField === uploadField ? (
                        <Loader2
                          size={14}
                          className="cockpit-settings-spinner"
                        />
                      ) : (
                        <Upload size={14} />
                      )}
                    </button>
                  ) : null}
                  {canEdit && bgSelected === index && hasImg ? (
                    <div
                      className="split-composer-bg-tools"
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <NumField
                        label="Zoom"
                        value={frame.scale}
                        min={100}
                        max={280}
                        step={1}
                        unit="%"
                        disabled={!canEdit}
                        onChange={(n) => {
                          updateBgFrame(index, { scale: n })
                          commitBgFrame()
                        }}
                      />
                      <button
                        type="button"
                        className="split-composer-reset"
                        onClick={() => {
                          updateBgFrame(index, {
                            scale: 100,
                            x: 50,
                            y: 50,
                          })
                          commitBgFrame()
                        }}
                      >
                        Centralizar
                      </button>
                      <button
                        type="button"
                        className="split-composer-reset"
                        onClick={() => clearBgPanel(index)}
                      >
                        Remover
                      </button>
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>

          <div className="split-composer-overlay">
            {(style.text_blocks || []).map((block) => {
              const visibleHere =
                previewMode === 'desktop'
                  ? block.show_desktop !== false
                  : block.show_mobile !== false
              if (!visibleHere) return null
              const pos = previewMode === 'desktop' ? block.desktop : block.mobile
              const size =
                previewMode === 'desktop'
                  ? block.font_size_desktop
                  : block.font_size_mobile
              const selected = selectedBlockId === block.id
              return (
                <div
                  key={block.id}
                  className={`split-composer-text-block ${
                    canEdit ? 'is-draggable' : ''
                  } ${selected ? 'is-selected' : ''}`}
                  style={{
                    left: `${pos.x}%`,
                    top: `${pos.y}%`,
                    width: `${pos.width}%`,
                    transform: `translate(-50%, -50%) rotate(${pos.rotate || 0}deg)`,
                    fontFamily: splitFontCss(block.font_family),
                    fontSize: `${size}rem`,
                    color: block.color,
                  }}
                  {...makeDragHandlers(`block:${block.id}`)}
                >
                  {selected ? renderElementFloat(pos.rotate || 0) : null}
                  <div className="split-composer-text-block-body">{block.text}</div>
                  {canEdit && selected ? renderTransformHandles(block.id) : null}
                </div>
              )
            })}

            {style.show_product_image !== false ? (
              <div
                className={`split-composer-product ${
                  canEdit ? 'is-draggable' : ''
                } ${productSelected ? 'is-selected' : ''}`}
                style={{
                  left: `${placement.x}%`,
                  top: `${placement.y}%`,
                  transform: `translate(-50%, -50%) rotate(${
                    placement.rotate || 0
                  }deg)`,
                  '--product-w': `${productWidthPct}%`,
                }}
                {...productDrag}
              >
                {productSelected
                  ? renderElementFloat(placement.rotate || 0)
                  : null}
                {productImg ? (
                  <img src={productImg} alt="" draggable={false} />
                ) : (
                  <div className="split-composer-product-empty">Produto</div>
                )}
                {canEdit && productSelected
                  ? renderTransformHandles('product')
                  : null}
              </div>
            ) : null}

            {style.show_product_name !== false ? (
              <div
                className={`split-composer-meta ${canEdit ? 'is-draggable' : ''} ${
                  nameSelected ? 'is-selected' : ''
                }`}
                style={{
                  left: `${placement.meta_x}%`,
                  top: `${placement.meta_y}%`,
                  width: `${placement.meta_width}%`,
                  transform: `translate(-50%, -50%) rotate(${
                    placement.meta_rotate || 0
                  }deg)`,
                  fontFamily: splitFontCss(style.font_family),
                  fontSize: `${fontSize}rem`,
                }}
                {...metaDrag}
              >
                {nameSelected
                  ? renderElementFloat(placement.meta_rotate || 0)
                  : null}
                <div className="split-composer-text-block-body">
                  {banner.product_name?.trim() ||
                    (banner.product_id
                      ? `Produto #${banner.product_id}`
                      : 'Selecione um produto')}
                </div>
                {canEdit && nameSelected ? renderTransformHandles('meta') : null}
              </div>
            ) : null}

            {style.show_product_price !== false ? (
              <div
                className={`split-composer-price ${
                  canEdit ? 'is-draggable' : ''
                } ${priceSelected ? 'is-selected' : ''}`}
                style={{
                  left: `${placement.price_x}%`,
                  top: `${placement.price_y}%`,
                  transform: 'translate(-50%, -50%)',
                  fontFamily: splitFontCss(style.price_font_family),
                  fontSize: `${priceFontSize}rem`,
                  color: style.price_color,
                }}
                {...priceDrag}
              >
                {priceSelected ? renderElementFloat(0) : null}
                {formatPrice(banner.product_price) || 'R$ 0,00'}
              </div>
            ) : null}

            {style.show_cta !== false ? (
              <div
                className={`split-composer-cta ${
                  canEdit ? 'is-draggable' : ''
                } ${ctaSelected ? 'is-selected' : ''} ${
                  style.cta_appearance === 'transparent' ? 'is-hotspot' : ''
                }`}
                style={{
                  left: `${placement.cta_x}%`,
                  top: `${placement.cta_y}%`,
                  transform: 'translate(-50%, -50%)',
                  ...(style.cta_appearance === 'transparent'
                    ? {
                        width: `${placement.cta_w}%`,
                        height: `${placement.cta_h}%`,
                        fontFamily: undefined,
                        fontSize: undefined,
                        color: undefined,
                        background: undefined,
                        borderRadius: undefined,
                      }
                    : {
                        fontFamily: splitFontCss(style.cta_font_family),
                        fontSize: `${ctaFontSize}rem`,
                        color: style.cta_color,
                        background: style.cta_bg_color,
                        borderRadius: `${style.cta_radius}rem`,
                      }),
                }}
                {...ctaDrag}
              >
                {ctaSelected ? renderElementFloat(0) : null}
                {style.cta_appearance === 'transparent' ? (
                  <span className="split-composer-cta-hotspot-label">
                    Área do botão
                  </span>
                ) : (
                  banner.cta_label?.trim() || 'Ver produto'
                )}
                {ctaSelected && style.cta_appearance === 'transparent'
                  ? renderCtaHandles()
                  : null}
              </div>
            ) : null}
          </div>
        </div>
        </div>
      </div>

      <div className="split-composer-controls">
        <div className="split-composer-toolbar-row">
          {previewMode === 'mobile' ? (
            <NumField
              label="Altura do banner (mobile)"
              value={style.viewport_height_mobile}
              min={45}
              max={95}
              step={1}
              unit="vh"
              disabled={!canEdit}
              onChange={(n) => {
                commitStyle({
                  ...style,
                  viewport_height_mobile: n,
                })
              }}
            />
          ) : null}
          <button
            type="button"
            className="split-composer-reset"
            disabled={!canEdit}
            onClick={() => {
              const key = previewMode === 'desktop' ? 'desktop' : 'mobile'
              commitStyle({
                ...style,
                [key]: { ...DEFAULT_SPLIT_STYLE[key] },
                ...(previewMode === 'desktop'
                  ? {
                      font_size_desktop: DEFAULT_SPLIT_STYLE.font_size_desktop,
                      price_font_size_desktop:
                        DEFAULT_SPLIT_STYLE.price_font_size_desktop,
                      cta_font_size_desktop:
                        DEFAULT_SPLIT_STYLE.cta_font_size_desktop,
                    }
                  : {
                      font_size_mobile: DEFAULT_SPLIT_STYLE.font_size_mobile,
                      price_font_size_mobile:
                        DEFAULT_SPLIT_STYLE.price_font_size_mobile,
                      cta_font_size_mobile:
                        DEFAULT_SPLIT_STYLE.cta_font_size_mobile,
                    }),
              })
            }}
          >
            Resetar {previewMode}
          </button>
          {lockedMode ? null : (
          <button
            type="button"
            className="split-composer-reset"
            disabled={!canEdit}
            onClick={() => {
              if (previewMode === 'desktop') {
                commitStyle({
                  ...style,
                  mobile: {
                    ...style.mobile,
                    x: style.desktop.x,
                    y: style.desktop.y,
                    scale: style.desktop.scale,
                    rotate: style.desktop.rotate,
                    height: style.desktop.height,
                    meta_x: style.desktop.meta_x,
                    meta_y: style.desktop.meta_y,
                    meta_width: style.desktop.meta_width,
                    meta_rotate: style.desktop.meta_rotate,
                    price_x: style.desktop.price_x,
                    price_y: style.desktop.price_y,
                    cta_x: style.desktop.cta_x,
                    cta_y: style.desktop.cta_y,
                    cta_w: style.desktop.cta_w,
                    cta_h: style.desktop.cta_h,
                  },
                  font_size_mobile: style.font_size_desktop,
                  price_font_size_mobile: style.price_font_size_desktop,
                  cta_font_size_mobile: style.cta_font_size_desktop,
                  text_blocks: (style.text_blocks || []).map((b) => ({
                    ...b,
                    mobile: { ...b.desktop },
                    font_size_mobile: b.font_size_desktop,
                  })),
                })
              } else {
                commitStyle({
                  ...style,
                  desktop: {
                    ...style.desktop,
                    x: style.mobile.x,
                    y: style.mobile.y,
                    scale: style.mobile.scale,
                    rotate: style.mobile.rotate,
                    height: style.mobile.height,
                    meta_x: style.mobile.meta_x,
                    meta_y: style.mobile.meta_y,
                    meta_width: style.mobile.meta_width,
                    meta_rotate: style.mobile.meta_rotate,
                    price_x: style.mobile.price_x,
                    price_y: style.mobile.price_y,
                    cta_x: style.mobile.cta_x,
                    cta_y: style.mobile.cta_y,
                    cta_w: style.mobile.cta_w,
                    cta_h: style.mobile.cta_h,
                  },
                  font_size_desktop: style.font_size_mobile,
                  price_font_size_desktop: style.price_font_size_mobile,
                  cta_font_size_desktop: style.cta_font_size_mobile,
                  text_blocks: (style.text_blocks || []).map((b) => ({
                    ...b,
                    desktop: { ...b.mobile },
                    font_size_desktop: b.font_size_mobile,
                  })),
                })
              }
            }}
          >
            Copiar → {previewMode === 'desktop' ? 'mobile' : 'desktop'}
          </button>
          )}
        </div>

        {(editorSection === 'name' ||
          editorSection === 'price' ||
          editorSection === 'cta') && (
        <div className="split-composer-style-panels">
          {editorSection === 'name' ? (
          <div className="split-composer-style-group">
            <div className="split-composer-style-head">
              <p className="split-composer-style-title">Nome</p>
              <label className="storefront-settings-active split-composer-toggle">
                <input
                  type="checkbox"
                  checked={style.show_product_name !== false}
                  disabled={!canEdit}
                  onChange={(e) =>
                    commitStyle({ ...style, show_product_name: e.target.checked })
                  }
                />
                Mostrar
              </label>
            </div>
            <div className="split-composer-style-fields">
              <label className="split-composer-field split-composer-field--font">
                <span className="split-composer-field-label">Fonte</span>
                <FontFamilyPicker
                  value={style.font_family}
                  disabled={!canEdit}
                  onChange={(family) =>
                    commitStyle({ ...style, font_family: family })
                  }
                />
              </label>
              <NumField
                label="Tamanho"
                value={fontSize}
                min={0.75}
                max={previewMode === 'desktop' ? 3.5 : 2.5}
                unit="rem"
                disabled={!canEdit}
                onChange={(n) => {
                  const key =
                    previewMode === 'desktop'
                      ? 'font_size_desktop'
                      : 'font_size_mobile'
                  commitStyle({ ...style, [key]: n })
                }}
              />
              <NumField
                label="Largura"
                value={placement.meta_width}
                min={12}
                max={92}
                step={0.5}
                unit="%"
                disabled={!canEdit}
                onChange={(n) => {
                  const key = previewMode === 'desktop' ? 'desktop' : 'mobile'
                  commitStyle({
                    ...style,
                    [key]: { ...placement, meta_width: n },
                  })
                }}
              />
              <NumField
                label="Rotação"
                value={placement.meta_rotate}
                min={-180}
                max={180}
                step={1}
                unit="°"
                disabled={!canEdit}
                onChange={(n) => {
                  const key = previewMode === 'desktop' ? 'desktop' : 'mobile'
                  commitStyle({
                    ...style,
                    [key]: { ...placement, meta_rotate: n },
                  })
                }}
              />
            </div>
          </div>
          ) : null}

          {editorSection === 'price' ? (
          <div className="split-composer-style-group">
            <div className="split-composer-style-head">
              <p className="split-composer-style-title">Preço</p>
              <label className="storefront-settings-active split-composer-toggle">
                <input
                  type="checkbox"
                  checked={style.show_product_price !== false}
                  disabled={!canEdit}
                  onChange={(e) =>
                    commitStyle({
                      ...style,
                      show_product_price: e.target.checked,
                    })
                  }
                />
                Mostrar
              </label>
            </div>
            <div className="split-composer-style-fields">
              <label className="split-composer-field split-composer-field--font">
                <span className="split-composer-field-label">Fonte</span>
                <FontFamilyPicker
                  value={style.price_font_family}
                  disabled={!canEdit}
                  onChange={(family) =>
                    commitStyle({ ...style, price_font_family: family })
                  }
                />
              </label>
              <NumField
                label="Tamanho"
                value={priceFontSize}
                min={0.6}
                max={previewMode === 'desktop' ? 3.5 : 2.5}
                unit="rem"
                disabled={!canEdit}
                onChange={(n) => {
                  const key =
                    previewMode === 'desktop'
                      ? 'price_font_size_desktop'
                      : 'price_font_size_mobile'
                  commitStyle({ ...style, [key]: n })
                }}
              />
              <ColorField
                label="Cor"
                value={style.price_color || '#ecfdf5'}
                disabled={!canEdit}
                onChange={(color) =>
                  commitStyle({ ...style, price_color: color })
                }
              />
            </div>
          </div>
          ) : null}

          {editorSection === 'cta' ? (
          <div className="split-composer-style-group">
            <div className="split-composer-style-head">
              <p className="split-composer-style-title">Botão</p>
              <label className="storefront-settings-active split-composer-toggle">
                <input
                  type="checkbox"
                  checked={style.show_cta !== false}
                  disabled={!canEdit}
                  onChange={(e) =>
                    commitStyle({ ...style, show_cta: e.target.checked })
                  }
                />
                Ativo
              </label>
            </div>
            {style.show_cta !== false ? (
              <>
                <div
                  className="split-composer-appearance"
                  role="group"
                  aria-label="Aparência do botão"
                >
                  <button
                    type="button"
                    className={
                      style.cta_appearance !== 'transparent' ? 'is-active' : ''
                    }
                    disabled={!canEdit}
                    onClick={() =>
                      commitStyle({ ...style, cta_appearance: 'solid' })
                    }
                  >
                    Visível
                  </button>
                  <button
                    type="button"
                    className={
                      style.cta_appearance === 'transparent' ? 'is-active' : ''
                    }
                    disabled={!canEdit}
                    onClick={() =>
                      commitStyle({ ...style, cta_appearance: 'transparent' })
                    }
                  >
                    Transparente
                  </button>
                </div>
                {style.cta_appearance === 'transparent' ? (
                  <p className="storefront-settings-legacy-hint">
                    Hotspot invisível na loja. No editor aparece tracejado —
                    ajuste posição e tamanho da área de clique.
                  </p>
                ) : null}
                <div className="split-composer-style-fields">
                  {style.cta_appearance === 'transparent' ? (
                    <>
                      <NumField
                        label="Largura"
                        value={placement.cta_w}
                        min={8}
                        max={90}
                        step={1}
                        unit="%"
                        disabled={!canEdit}
                        onChange={(n) => {
                          const key =
                            previewMode === 'desktop' ? 'desktop' : 'mobile'
                          commitStyle({
                            ...style,
                            [key]: { ...placement, cta_w: n },
                          })
                        }}
                      />
                      <NumField
                        label="Altura"
                        value={placement.cta_h}
                        min={4}
                        max={60}
                        step={1}
                        unit="%"
                        disabled={!canEdit}
                        onChange={(n) => {
                          const key =
                            previewMode === 'desktop' ? 'desktop' : 'mobile'
                          commitStyle({
                            ...style,
                            [key]: { ...placement, cta_h: n },
                          })
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <label className="split-composer-field split-composer-field--font">
                        <span className="split-composer-field-label">Fonte</span>
                        <FontFamilyPicker
                          value={style.cta_font_family}
                          disabled={!canEdit}
                          onChange={(family) =>
                            commitStyle({ ...style, cta_font_family: family })
                          }
                        />
                      </label>
                      <NumField
                        label="Tamanho"
                        value={ctaFontSize}
                        min={0.55}
                        max={previewMode === 'desktop' ? 2.5 : 2}
                        unit="rem"
                        disabled={!canEdit}
                        onChange={(n) => {
                          const key =
                            previewMode === 'desktop'
                              ? 'cta_font_size_desktop'
                              : 'cta_font_size_mobile'
                          commitStyle({ ...style, [key]: n })
                        }}
                      />
                      <ColorField
                        label="Texto"
                        value={style.cta_color || '#37971c'}
                        disabled={!canEdit}
                        onChange={(color) =>
                          commitStyle({ ...style, cta_color: color })
                        }
                      />
                      <ColorField
                        label="Fundo"
                        value={style.cta_bg_color || '#ffffff'}
                        disabled={!canEdit}
                        onChange={(color) =>
                          commitStyle({ ...style, cta_bg_color: color })
                        }
                      />
                      <NumField
                        label="Arredond."
                        value={style.cta_radius}
                        min={0}
                        max={3}
                        unit="rem"
                        disabled={!canEdit}
                        onChange={(n) =>
                          commitStyle({ ...style, cta_radius: n })
                        }
                      />
                    </>
                  )}
                </div>
              </>
            ) : null}
          </div>
          ) : null}
        </div>
        )}
      </div>

      {editorSection === 'texts' ? (
      <div className="split-composer-text-blocks">
        <div className="split-composer-text-blocks-head">
          <strong>Blocos de texto</strong>
          <button
            type="button"
            className="split-composer-add-block"
            disabled={!canEdit || (style.text_blocks || []).length >= MAX_TEXT_BLOCKS}
            onClick={addTextBlock}
          >
            <Plus size={14} />
            Adicionar texto
          </button>
        </div>
        {(style.text_blocks || []).length === 0 ? (
          <p className="storefront-settings-legacy-hint">
            Clique no bloco para selecionar. Arraste o texto para mover, as
            quinas para o tamanho da fonte, as laterais para a largura e o ponto
            verde para girar.
          </p>
        ) : (
          <ul className="split-composer-text-list">
            {(style.text_blocks || []).map((block) => (
              <li key={block.id}>
                <button
                  type="button"
                  className={
                    selectedBlockId === block.id
                      ? 'split-composer-text-item is-active'
                      : 'split-composer-text-item'
                  }
                  onClick={() => setSelectedBlockId(block.id)}
                >
                  {block.text}
                </button>
                <div className="split-composer-device-toggles" role="group" aria-label="Visível em">
                  <button
                    type="button"
                    className={`split-composer-device-btn ${
                      block.show_desktop !== false ? 'is-on' : ''
                    }`}
                    disabled={!canEdit}
                    title={
                      block.show_desktop !== false
                        ? 'Visível no computador — clique para ocultar'
                        : 'Oculto no computador — clique para mostrar'
                    }
                    aria-pressed={block.show_desktop !== false}
                    onClick={() =>
                      updateTextBlock(block.id, {
                        show_desktop: block.show_desktop === false,
                      })
                    }
                  >
                    <Monitor size={14} />
                  </button>
                  <button
                    type="button"
                    className={`split-composer-device-btn ${
                      block.show_mobile !== false ? 'is-on' : ''
                    }`}
                    disabled={!canEdit}
                    title={
                      block.show_mobile !== false
                        ? 'Visível no celular — clique para ocultar'
                        : 'Oculto no celular — clique para mostrar'
                    }
                    aria-pressed={block.show_mobile !== false}
                    onClick={() =>
                      updateTextBlock(block.id, {
                        show_mobile: block.show_mobile === false,
                      })
                    }
                  >
                    <Smartphone size={14} />
                  </button>
                </div>
                {canEdit ? (
                  <button
                    type="button"
                    className="storefront-settings-icon-btn"
                    onClick={() => removeTextBlock(block.id)}
                    aria-label="Remover bloco"
                  >
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {selectedBlock ? (
          <div className="split-composer-text-editor">
            <label className="split-composer-field split-composer-field--grow">
              <span className="split-composer-field-label">Texto</span>
              <input
                type="text"
                value={selectedBlock.text}
                disabled={!canEdit}
                maxLength={200}
                onChange={(e) =>
                  updateTextBlock(selectedBlock.id, { text: e.target.value })
                }
              />
            </label>
            <label className="split-composer-field split-composer-field--font">
              <span className="split-composer-field-label">Fonte</span>
              <FontFamilyPicker
                value={selectedBlock.font_family}
                disabled={!canEdit}
                onChange={(family) =>
                  updateTextBlock(selectedBlock.id, { font_family: family })
                }
              />
            </label>
            <NumField
              label="Tamanho"
              value={
                previewMode === 'desktop'
                  ? selectedBlock.font_size_desktop
                  : selectedBlock.font_size_mobile
              }
              min={0.6}
              max={previewMode === 'desktop' ? 4 : 3}
              unit="rem"
              disabled={!canEdit}
              onChange={(n) => {
                const key =
                  previewMode === 'desktop'
                    ? 'font_size_desktop'
                    : 'font_size_mobile'
                updateTextBlock(selectedBlock.id, { [key]: n })
              }}
            />
            <NumField
              label="Largura"
              value={
                (previewMode === 'desktop'
                  ? selectedBlock.desktop?.width
                  : selectedBlock.mobile?.width) ?? 40
              }
              min={12}
              max={92}
              step={0.5}
              unit="%"
              disabled={!canEdit}
              onChange={(n) => {
                const key = previewMode === 'desktop' ? 'desktop' : 'mobile'
                updateTextBlock(selectedBlock.id, {
                  [key]: { ...selectedBlock[key], width: n },
                })
              }}
            />
            <NumField
              label="Rotação"
              value={
                (previewMode === 'desktop'
                  ? selectedBlock.desktop?.rotate
                  : selectedBlock.mobile?.rotate) ?? 0
              }
              min={-180}
              max={180}
              step={1}
              unit="°"
              disabled={!canEdit}
              onChange={(n) => {
                const key = previewMode === 'desktop' ? 'desktop' : 'mobile'
                updateTextBlock(selectedBlock.id, {
                  [key]: { ...selectedBlock[key], rotate: n },
                })
              }}
            />
            <ColorField
              label="Cor"
              value={selectedBlock.color || '#ffffff'}
              disabled={!canEdit}
              onChange={(color) =>
                updateTextBlock(selectedBlock.id, { color })
              }
            />
          </div>
        ) : null}
      </div>
      ) : null}

      <div className="split-composer-bottom-row">
      <div className="split-composer-product-block">
        <label className="storefront-settings-label">Produto em destaque</label>
        {banner.product_id ? (
          <div className="split-composer-selected">
            {productImg ? <img src={productImg} alt="" /> : null}
            <div>
              <strong>{banner.product_name || `Produto #${banner.product_id}`}</strong>
              {formatPrice(banner.product_price) ? (
                <span>{formatPrice(banner.product_price)}</span>
              ) : null}
            </div>
            {canEdit ? (
              <button
                type="button"
                className="storefront-settings-icon-btn"
                onClick={() =>
                  patchBanner({
                    product_id: null,
                    product_name: '',
                    product_price: null,
                    product_image_url: '',
                    product_image_id: null,
                  })
                }
                aria-label="Remover produto"
              >
                ×
              </button>
            ) : null}
          </div>
        ) : canEdit ? (
          <ERPProductSearch
            placeholder="Buscar produto para o centro do banner..."
            onSelect={handleSelectProduct}
          />
        ) : (
          <p className="storefront-settings-legacy-hint">Nenhum produto selecionado.</p>
        )}

        {banner.product_id ? (
          <div className="split-composer-gallery">
            <span className="split-composer-gallery-label">
              Foto do produto no banner
            </span>
            {galleryLoading ? (
              <Loader2 size={16} className="cockpit-settings-spinner" />
            ) : (
              <div className="split-composer-gallery-grid">
                <button
                  type="button"
                  className={`split-composer-gallery-thumb split-composer-gallery-none ${
                    style.show_product_image === false ? 'is-selected' : ''
                  }`}
                  disabled={!canEdit}
                  onClick={clearProductImage}
                  title="Sem foto do produto"
                >
                  Sem foto
                </button>
                {gallery.length === 0 ? (
                  <p className="storefront-settings-legacy-hint">
                    Sem imagens na galeria. Adicione fotos no cadastro do
                    produto.
                  </p>
                ) : (
                  gallery.map((img) => {
                    const selected =
                      style.show_product_image !== false &&
                      (banner.product_image_id != null
                        ? Number(banner.product_image_id) === Number(img.id)
                        : gallery[0]?.id === img.id)
                    return (
                      <button
                        key={img.id}
                        type="button"
                        className={`split-composer-gallery-thumb ${
                          selected ? 'is-selected' : ''
                        }`}
                        disabled={!canEdit}
                        onClick={() => selectGalleryImage(img)}
                      >
                        <img src={img.image_url} alt="" />
                      </button>
                    )
                  })
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>
      </div>
    </div>
  )
}
